import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase, checkSupabaseConnection } from '../lib/supabase';
import { Customer } from '../types';
import { createOrGetCustomer } from '../api';

interface SupabaseContextType {
  isConnected: boolean;
  currentCustomer: Customer | null;
  setCurrentCustomer: (customer: Customer | null) => void;
  loginCustomer: (phone: string, name: string, email?: string) => Promise<Customer | null>;
  logoutCustomer: () => void;
}

const SupabaseContext = createContext<SupabaseContextType | undefined>(undefined);

export const SupabaseProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState<Customer | null>(() => {
    const saved = localStorage.getItem('ff_customer');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    checkSupabaseConnection().then(setIsConnected);
  }, []);

  useEffect(() => {
    if (currentCustomer) {
      localStorage.setItem('ff_customer', JSON.stringify(currentCustomer));
    } else {
      localStorage.removeItem('ff_customer');
    }
  }, [currentCustomer]);

  const loginCustomer = async (phone: string, name: string, email?: string) => {
    const customer = await createOrGetCustomer(name, phone, email);
    if (customer) {
      setCurrentCustomer(customer);
    }
    return customer;
  };

  const logoutCustomer = () => {
    setCurrentCustomer(null);
  };

  return (
    <SupabaseContext.Provider
      value={{
        isConnected,
        currentCustomer,
        setCurrentCustomer,
        loginCustomer,
        logoutCustomer
      }}
    >
      {children}
    </SupabaseContext.Provider>
  );
};

export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (!context) throw new Error('useSupabase must be used within a SupabaseProvider');
  return context;
};